import { Injectable } from '@angular/core';
import { GlobalSettingsService } from '@core/globalSettings.service';
import { EupRoutesService } from '@core/eupRoutes.service';
import { ReferralResponse } from '@shared/generalInterfaces';
import { EupHttpHandler } from '@core/eupHttpHandler.service';
import { Observable, BehaviorSubject } from 'rxjs';
import { LabRxExtensionService } from '../labRxExtension/labRxExtension.service';
import { IRxUILabNote } from '@interfaces/IEUPIteroNote';
import { SoftwareOptionsForCompany, RowStatus } from '@shared/enums';
import { SoftwareOptionsService } from '@core/companySoftwareOptions/service/softwareOptions.service';
import { map, take } from 'rxjs/operators';
import { IOrderInformation } from '@interfaces/IOrderInformation';

@Injectable()
export class RxService {
	private http: EupHttpHandler;

	IsSavingRx: boolean;
	IsOrderProceeding: boolean;
	newNoteObservable$: BehaviorSubject<IRxUILabNote[]>;
	rxIdsForPrintObservable: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([]);

	constructor(
		private globalSettings: GlobalSettingsService,
		http: EupHttpHandler,
		public eupRoutesService: EupRoutesService,
		public labRxExtensionService: LabRxExtensionService,
		private softwareOptionsService: SoftwareOptionsService
	) {
		this.http = http;
		this.handleNewNotes();
	}

	shouldRxBeReadOnly(referral: ReferralResponse, isTakenByScanner): boolean {
		if (!referral) {
			return isTakenByScanner;
		}

		const companySoftwareOptions = this.globalSettings.get().companySoftwareOptions;
		const isReferralPractice = this.softwareOptionsService.isSoftwareOptionExists(
			SoftwareOptionsForCompany.referralWorkflowPractice,
			companySoftwareOptions
		);
		const { rowStatusId, isLoadedByScanningCenter } = referral;
		const isReferralRxShouldBeReadOnly = isReferralPractice && !(rowStatusId === RowStatus.Active && !isLoadedByScanningCenter);

		return isTakenByScanner || isReferralRxShouldBeReadOnly;
	}

	shouldBeAbleToDeleteRx(referral: ReferralResponse, isTakenByScanner): boolean {
		if (!referral) {
			return !isTakenByScanner;
		}

		const companySoftwareOptions = this.globalSettings.get().companySoftwareOptions;
		const isReferralPractice = this.softwareOptionsService.isSoftwareOptionExists(
			SoftwareOptionsForCompany.referralWorkflowPractice,
			companySoftwareOptions
		);
		const { rowStatusId } = referral;
		const isReferralCouldBeDeleted = !(isReferralPractice && rowStatusId === RowStatus.Resolved);

		return !isTakenByScanner && isReferralCouldBeDeleted;
	}

	public validateClinicRx(rxId: string): Observable<boolean> {
		const url = `${this.eupRoutesService.rx.validateClinicRxUrl}/${rxId}`;

		return this.http.get(url);
	}

	setReadyForDownloadAsDownloadedForLab(orderId: number) {
		const url = this.eupRoutesService.orders.setReadyForDownloadAsDownloadedForLab;

		return this.http.post(url, { orderId }, undefined, true, false);
	}

	processSendToLab(orderId: number) {
		const url = `${this.eupRoutesService.lab.processSendToLab}?orderHeaderId=${orderId}`;

		return this.http.get(url);
	}

	printOrdersRx(orderIds: string): Observable<string> {
		const url = `${this.eupRoutesService.orders.updatePrintedOrdersUrl}`;
		const printOrdersPost = this.http.post(url, { orderIds }, undefined, true, false);
		const rxIdsForPrint = orderIds === '' ? [] : orderIds.split(',')?.map(rxId => parseInt(rxId));

		this.rxIdsForPrintObservable.next(rxIdsForPrint);

		printOrdersPost.pipe(take(1)).subscribe();

		return this.rxIdsForPrintObservable.asObservable().pipe(
			take(1),
			map(rxIds => rxIds.join(','))
		);
	}

	setOrderInformation(orderInformation: IOrderInformation): void {
		this.labRxExtensionService.initOrderInformation(orderInformation);
	}

	canOrderPhysicalModel(companyId: number): Observable<boolean> {
		const route = `${this.eupRoutesService.orderInformation.canOrderPhysicalModel}${companyId}`;

		return this.http.get(route, null, false, true).pipe(map(response => response.Result));
	}

	private handleNewNotes() {
		this.newNoteObservable$ = new BehaviorSubject([]);
	}
}
